import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Share, MoreVertical, MonitorDown, X, PlusSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { usePwaInstall } from '@/lib/pwaInstall';

const Step = ({ n, icon: Icon, text }) => (
  <li className="flex items-start gap-3">
    <span className="w-6 h-6 rounded-full bg-emerald-600 text-white text-xs font-bold flex items-center justify-center shrink-0">{n}</span>
    <span className="text-sm text-slate-700 flex items-center gap-1.5 flex-wrap">{Icon && <Icon size={16} className="text-slate-500 shrink-0" />}{text}</span>
  </li>
);

export const InstallInstructionsDialog = ({ open, onOpenChange, platform }) => {
  const { t } = useTranslation();
  const steps = {
    ios: [[Share, t('pwa.ios1')], [PlusSquare, t('pwa.ios2')], [null, t('pwa.ios3')]],
    android: [[MoreVertical, t('pwa.android1')], [Download, t('pwa.android2')], [null, t('pwa.android3')]],
    desktop: [[MonitorDown, t('pwa.desktop1')], [null, t('pwa.desktop2')]],
  }[platform] || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="pwa-install-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Download size={18} className="text-emerald-600" /> {t('pwa.title')}</DialogTitle>
          <DialogDescription>{t('pwa.desc')}</DialogDescription>
        </DialogHeader>
        <ol className="space-y-3 mt-1" data-testid={`pwa-steps-${platform}`}>
          {steps.map(([icon, text], i) => <Step key={i} n={i + 1} icon={icon} text={text} />)}
        </ol>
        <p className="text-xs text-slate-500 mt-2">{t('pwa.note')}</p>
        <Button onClick={() => onOpenChange(false)} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white mt-1" data-testid="pwa-install-dialog-close">{t('common.close')}</Button>
      </DialogContent>
    </Dialog>
  );
};

const InstallAppButton = ({ variant = 'full', className = '' }) => {
  const { t } = useTranslation();
  const { canPrompt, installed, promptInstall, platform } = usePwaInstall();
  const [showHelp, setShowHelp] = useState(false);
  if (installed) return null;
  const onClick = async () => {
    const outcome = await promptInstall();
    if (outcome === 'unavailable') setShowHelp(true);
  };
  return (
    <>
      {variant === 'icon' ? (
        <Button variant="ghost" size="sm" onClick={onClick} title={t('pwa.install')} aria-label={t('pwa.install')} data-testid="pwa-install-icon-button" className={`text-emerald-700 hover:bg-emerald-50 ${className}`}>
          <Download size={18} />
          <span className="hidden lg:inline text-xs">{t('pwa.install')}</span>
        </Button>
      ) : (
        <Button variant="outline" onClick={onClick} data-testid="pwa-install-button" className={`border-emerald-200 text-emerald-700 hover:bg-emerald-50 ${className}`}>
          <Download size={16} className="mr-2" /> {canPrompt ? t('pwa.install') : t('pwa.howTo')}
        </Button>
      )}
      <InstallInstructionsDialog open={showHelp} onOpenChange={setShowHelp} platform={platform} />
    </>
  );
};

const BANNER_KEY = 'rs_pwa_banner_dismissed_at';
export const InstallBanner = () => {
  const { t } = useTranslation();
  const { installed, promptInstall, platform } = usePwaInstall();
  const [showHelp, setShowHelp] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    const at = Number(localStorage.getItem(BANNER_KEY) || 0);
    return at && Date.now() - at < 7 * 24 * 3600 * 1000;
  });
  if (installed || dismissed) return null;
  const dismiss = () => { localStorage.setItem(BANNER_KEY, String(Date.now())); setDismissed(true); };
  const onInstall = async () => {
    const outcome = await promptInstall();
    if (outcome === 'unavailable') setShowHelp(true);
    else if (outcome === 'accepted') dismiss();
  };
  return (
    <>
      <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-sm z-40 bg-slate-900 text-white rounded-xl shadow-2xl p-4 flex items-center gap-3 fade-in-up" data-testid="pwa-install-banner">
        <div className="w-10 h-10 rounded-lg bg-emerald-600 flex items-center justify-center shrink-0"><Download size={20} /></div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-tight">{t('pwa.bannerTitle')}</p>
          <p className="text-xs text-slate-300 leading-snug">{t('pwa.bannerDesc')}</p>
        </div>
        <Button size="sm" onClick={onInstall} data-testid="pwa-banner-install" className="bg-emerald-600 hover:bg-emerald-500 text-white shrink-0">{t('pwa.install')}</Button>
        <button onClick={dismiss} aria-label={t('common.close')} data-testid="pwa-banner-dismiss" className="text-slate-400 hover:text-white shrink-0"><X size={16} /></button>
      </div>
      <InstallInstructionsDialog open={showHelp} onOpenChange={setShowHelp} platform={platform} />
    </>
  );
};

export default InstallAppButton;
